/**
 * Next Of Kin card (dynamic Application Form section, TT-65). Presence-driven:
 * renders only when the property step carries next-of-kin data — the form only
 * collects it when the template section is on, and legacy applications never
 * collected it, so an empty card can't appear either way. Fields mirror the
 * PropertyStep contact shape (name split preferred, whole-name fallback).
 */
import { splitName } from '#app/utils/application-detail-v2/display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import { FieldBox, SectionCard } from '../primitives'

export function nextOfKinHasData(
	propertyStep: ParsedStepData['property'],
): boolean {
	const kin = propertyStep?.nextOfKin
	if (!kin) return false
	return [
		kin.name,
		kin.firstName,
		kin.lastName,
		kin.email,
		kin.phone,
		kin.relationship,
	].some((value) => typeof value === 'string' && value.trim() !== '')
}

export function NextOfKin({
	propertyStep,
}: {
	propertyStep: ParsedStepData['property']
}) {
	if (!nextOfKinHasData(propertyStep)) return null
	const kin = propertyStep?.nextOfKin ?? {}
	const fallback = splitName(kin.name ?? '')
	const firstName = kin.firstName?.trim() || fallback.firstName
	const lastName = kin.lastName?.trim() || fallback.lastName

	return (
		<SectionCard
			// Distinct Next Of Kin glyph (client 2026-09-07) — the old asset was a
			// byte-identical copy of Personal Details.
			iconSrc="/img/next-of-kin-icon.svg"
			title="Next Of Kin"
			columns={2}
		>
			{/* Figma order (QA 2026-09): names, then contact, Relationship last. */}
			<FieldBox label="First Name" value={firstName} />
			<FieldBox label="Last Name" value={lastName} />
			<FieldBox label="Email" value={kin.email} />
			<FieldBox
				label="Phone"
				value={formatPhoneNumberWithFallback(kin.phone, kin.phoneCountryCode)}
			/>
			<FieldBox label="Relationship" value={kin.relationship} />
		</SectionCard>
	)
}
